import React, { ReactElement, useMemo } from 'react';
import Table, { createTd, createTh } from 'components/tables/Table';
import {
    ColumnColor,
    ColumnHeadInterface,
    ColumnSize,
    ColumnValueInterface,
    ColumnValueWeight,
} from 'components/interfaces/GeneralInterface';
import { useTranslation } from 'utils/localization';
import { toDate } from 'utils/helpers/datetime';
import { CoachDetailLicenceItemApiInterface } from 'submodules/api_middleware';
import { isCoachLicenceActive } from 'utils/helpers/coach';

interface Props {
    entries: CoachDetailLicenceItemApiInterface[];
    title: string;
}

/**
 * Passport-style licence block for the coach Osobní údaje page. Reuses the
 * same `Table` card as the player Passport / referee Licences blocks so the
 * three person detail pages stay visually consistent.
 *
 * Data source is the IS1-derived `coach.licences` array already returned by
 * GET /coaches/{id}, so this component does no extra HTTP work.
 */
const CoachLicencesBlock = ({ entries, title }: Props): ReactElement => {
    const { t } = useTranslation();

    const columns: ColumnHeadInterface[] = useMemo(
        () => [
            createTh(t, 'RefereeProfile.licenceType', ColumnSize.M),
            createTh(t, 'CoachPage.licenceNumber', ColumnSize.M),
            createTh(t, 'CoachPage.committee', ColumnSize.M),
            createTh(t, 'CoachPage.clubs.validFrom', ColumnSize.M),
            createTh(t, 'CoachPage.clubs.validTo', ColumnSize.M),
            createTh(t, 'RefereeProfile.status', ColumnSize.M),
        ],
        [t]
    );

    const data: ColumnValueInterface[][] = (entries ?? []).map((l) => {
        const isActive = isCoachLicenceActive(l.valid_to);

        return [
            createTd(l.licence_type ?? '–', ColumnValueWeight.BOLD),
            createTd(l.licence_number ?? '–'),
            createTd(l.committee ?? '–'),
            createTd(toDate(l.valid_from)),
            createTd(toDate(l.valid_to), ColumnValueWeight.BOLD),
            isActive
                ? createTd(
                      t('RefereeProfile.active') as string,
                      ColumnValueWeight.BOLD,
                      ColumnColor.GREEN
                  )
                : createTd(
                      t('CoachPage.licenceInactive') as string,
                      ColumnValueWeight.BOLD,
                      ColumnColor.RED
                  ),
        ];
    });

    return (
        <Table
            columns={columns}
            data={data}
            cardTitle={title}
            noEntriesSubText={t('CoachPage.licencesEmpty')}
        />
    );
};

export default CoachLicencesBlock;
